<template>
  <div class="markdown-container">
    <VueMarkdown :markdown="compiledVueMarkdown" :remark-plugins="remarkPlugins" :html="true" allow-html="true"
      style="background-color: transparent;line-height: 1.6;width: 100% !important;" class="markdownImg">
      
      <!-- เพิ่ม template สำหรับ custom file display -->
      <template #img="{ src, alt, title }">
        <div v-if="isFileDisplay(src, alt)" class="file-display-wrapper">
          <img :src="extractFileSrc(src)" width="16" height="16" style="vertical-align: middle;" class="file-icon" :alt="alt" />
          <small class="filename">{{ alt }}</small>
        </div>
        <img v-else :src="src" :alt="alt" :title="title" />
      </template>

      <template #code="{ ...props }">
        <div class="code-block-modern" v-if="props.language">
          <div class="code-header">
            <div class="code-language">
              <span style="color: gray;">{{ getLanguageDisplayName(props.language) }}</span>
            </div>
            <v-btn icon size="18" class="copy-btn" @click="copyCodeBlock(props.content)"
              v-tooltip="copiedStates[props.content] ? 'Copied!' : 'Copy code'">
              <v-icon size="18">{{ copiedStates[props.content] ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            </v-btn>
          </div>

          <!-- Code content -->
          <div class="code-content">
            <pre class="code-pre"> <code 
                class="hljs code-element" style="margin: 0 !important;padding: 0 !important;"
                v-html="highlightCode(props.content, props.language)"
              ></code> </pre>
          </div>
        </div>

        <template v-if="!props.language">
          <div v-if="props.content && props.content.length > 60" class="inline-code-scrollable-block">
            {{ props.content }}
          </div>
          <span v-else class="inline-text-bg">{{ props.content }}</span>
        </template>
      </template>

      <!-- New template for links -->
      <template #a="{ href, children }">
        <a :href="href" target="_blank" rel="noopener noreferrer">
          <component :is="children" />
        </a>
      </template>
      <!-- ปรับปรุง Template สำหรับตาราง -->
      <template #table="{ children }">
        <div class="table-container">
          <table class="markdown-table">
            <thead class="sr-only">
              <tr>
                <th scope="col">Markdown content</th>
              </tr>
            </thead>
            <component :is="children" />
          </table>
        </div>
      </template>

      <!-- เพิ่ม Template สำหรับ๝ถวของตาราง -->
      <template #tr="{ children, isHead }">
        <tr :class="{ 'table-head-row': isHead, 'table-body-row': !isHead }">
          <component :is="children" />
        </tr>
      </template>

      <!-- เพิ่ม Template สำหรับ header cell -->
      <template #th="{ children }">
        <th class="table-header-cell">
          <component :is="children" />
        </th>
      </template>

      <!-- เพิ่ม Template สำหรับ data cell -->
      <template #td="{ children }">
        <td :class="['table-data-cell', getContentClass(children)]">
          <component :is="children" />
        </td>
      </template>
    </VueMarkdown>
  </div>
</template>

<script>
// import remarkHtml from 'remark-html'
import remarkBreaks from 'remark-breaks'
import { VueMarkdown } from '@crazydos/vue-markdown'
import hljs from 'highlight.js'
import 'highlight.js/styles/github.css'
import DOMPurify from 'dompurify'
import remarkGfm from 'remark-gfm'
import { visit } from 'unist-util-visit'

const remarkStripHeadings = () => {
  return (tree) => {
    visit(tree, 'heading', (node, idx, parent) => {
      parent.children.splice(
        idx,
        1,
        // { type: 'paragraph', children: [ { type: 'strong', children: node.children } ] }

        { type: 'paragraph', children: node.children }
      )
    })
  }
}

export default {
  name: 'MarkedMarkdown',
  components: {
    VueMarkdown
  },
  props: {
    content: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      remarkPlugins: [remarkGfm, remarkStripHeadings, remarkBreaks],
      copiedStates: {},
      languageMap: {
        // Programming Languages
        'javascript': 'JavaScript',
        'js': 'JavaScript',
        'typescript': 'TypeScript',
        'ts': 'TypeScript',
        'csharp': 'C#',
        'cs': 'C#',
        'c#': 'C#',
        'cpp': 'C++',
        'c++': 'C++',
        'c': 'C',
        'java': 'Java',
        'python': 'Python',
        'py': 'Python',
        'php': 'PHP',
        'ruby': 'Ruby',
        'rb': 'Ruby',
        'go': 'Go',
        'golang': 'Go',
        'rust': 'Rust',
        'rs': 'Rust',
        'swift': 'Swift',
        'kotlin': 'Kotlin',
        'kt': 'Kotlin',
        'scala': 'Scala',
        'r': 'R',
        'perl': 'Perl',
        'lua': 'Lua',
        'dart': 'Dart',
        'elixir': 'Elixir',
        'erlang': 'Erlang',
        'haskell': 'Haskell',
        'hs': 'Haskell',
        'clojure': 'Clojure',
        'fsharp': 'F#',
        'f#': 'F#',
        'vb': 'Visual Basic',
        'vbnet': 'VB.NET',
        'visualbasic': 'Visual Basic',
        'objectivec': 'Objective-C',
        'objc': 'Objective-C',

        // Web Technologies
        'html': 'HTML',
        'htm': 'HTML',
        'css': 'CSS',
        'scss': 'SCSS',
        'sass': 'Sass',
        'less': 'Less',
        'stylus': 'Stylus',
        'vue': 'Vue.js',
        'react': 'React',
        'jsx': 'React JSX',
        'tsx': 'TypeScript JSX',
        'angular': 'Angular',
        'svelte': 'Svelte',

        // Markup & Data
        'xml': 'XML',
        'json': 'JSON',
        'yaml': 'YAML',
        'yml': 'YAML',
        'toml': 'TOML',
        'ini': 'INI',
        'markdown': 'Markdown',
        'md': 'Markdown',
        'tex': 'LaTeX',
        'latex': 'LaTeX',

        // Shell & Scripts
        'bash': 'Bash',
        'sh': 'Shell',
        'shell': 'Shell',
        'powershell': 'PowerShell',
        'ps1': 'PowerShell',
        'cmd': 'Command Prompt',
        'batch': 'Batch',
        'bat': 'Batch',
        'zsh': 'Zsh',
        'fish': 'Fish',

        // Database
        'sql': 'SQL',
        'mysql': 'MySQL',
        'postgresql': 'PostgreSQL',
        'sqlite': 'SQLite',
        'plsql': 'PL/SQL',
        'tsql': 'T-SQL',
        'nosql': 'NoSQL',

        // Configuration & DevOps
        'dockerfile': 'Dockerfile',
        'docker': 'Docker',
        'nginx': 'Nginx',
        'apache': 'Apache',
        'terraform': 'Terraform',
        'vagrant': 'Vagrant',
        'ansible': 'Ansible',
        'jenkins': 'Jenkins',
        'gitlab-ci': 'GitLab CI',
        'github-actions': 'GitHub Actions',

        // Mobile
        'flutter': 'Flutter',
        'xamarin': 'Xamarin',

        // Game Development
        'unity': 'Unity C#',
        'unreal': 'UnrealScript',
        'gdscript': 'GDScript',

        // Other
        'makefile': 'Makefile',
        'cmake': 'CMake',
        'gradle': 'Gradle',
        'maven': 'Maven',
        'npm': 'NPM',
        'yarn': 'Yarn',
        'pip': 'Pip',
        'composer': 'Composer',
        'cargo': 'Cargo',
        'gem': 'RubyGems',

        // Assembly & Low Level
        'assembly': 'Assembly',
        'asm': 'Assembly',
        'nasm': 'NASM',
        'masm': 'MASM',

        // Functional Languages
        'lisp': 'Lisp',
        'scheme': 'Scheme',
        'ml': 'ML',
        'ocaml': 'OCaml',

        // Academic & Research
        'matlab': 'MATLAB',
        'mathematica': 'Mathematica',
        'maple': 'Maple',
        'sage': 'SageMath',
        'julia': 'Julia',

        // Web Assembly
        'wasm': 'WebAssembly',
        'wat': 'WebAssembly Text',

        // Default fallbacks
        'text': 'Text',
        'txt': 'Text',
        'plain': 'Plain Text',
        'code': 'Code',
        '': 'Code'
      }
    }
  },
  computed: {
    // Convert the Markdown content to HTML
    compiledMarkdown() {
      const safeContent = typeof this.content === 'string' ? this.content : String(this.content ?? '')
      const rawHtml = this.md.render(safeContent)
      const sanitizedHtml = DOMPurify.sanitize(rawHtml, { 
        ALLOWED_ATTR: ['target', 'href', 'rel', 'src', 'alt', 'width', 'height', 'style', 'class'] 
      })
      return sanitizedHtml
    },
    compiledVueMarkdown() {
      const safeContent = typeof this.content === 'string' ? this.content : String(this.content ?? '');
      let processed = safeContent.replace(/<br\s*\/?>/gi, '\n');
      processed = this.processFileDisplay(processed);
      
      // เพิ่มฝาร sanitize เพื่อให้๝น่ใจว่า HTML ปลอดภัย
      return DOMPurify.sanitize(processed, { 
        ALLOWED_TAGS: ['img', 'small', 'div', 'span', 'p', 'br', 'strong', 'em', 'code', 'pre', 'a', 'ul', 'ol', 'li', 'table', 'tr', 'td', 'th'],
        ALLOWED_ATTR: ['src', 'alt', 'width', 'height', 'style', 'class', 'href', 'target', 'rel']
      });
    }
  },
  methods: {
    processFileDisplay(content) {
      let showConsole = false;
      let returnContent = content.replace(
        /<img src="([^"]*)" width="(\d+)" height="(\d+)" style="([^"]*)"> <small>([^<]*)<\/small>/gi,
        (match, src, width, height, style, filename) => {
          console.log('original content:', content);
          showConsole = true;
          // ใช้ custom marker ๝ทน HTML
            return `![${filename}](${src})`;
        }
      );
      if(showConsole)
        console.log('processed content:', returnContent);
      return returnContent;
    },
    isFileDisplay(src, alt) {
      // ตรวจสอบว่าเป็น file icon หรือไม่
      return src && (src.includes('/assets/images/FileTypes/') || src.includes('/assets/')) && alt;
    },
    
    extractFileSrc(src) {
      return src;
    },
    getContentClass(children) {
      const textContent = this.getTextContent(children);
      const length = textContent.length;

      if (length > 100) {
        return 'extra-long';
      } else if (length > 50) {
        return 'long-content';
      } else {
        return 'short-content';
      }
    },
    getTextContent(children) {
      if (typeof children === 'string') {
        return children;
      }
      if (Array.isArray(children)) {
        return children.map(child =>
          typeof child === 'string' ? child : child.props?.children || ''
        ).join('');
      }
      return children?.props?.children || '';
    },
    highlightCode(code, lang) {
      try {
        return hljs.highlight(code, { language: lang, ignoreIllegals: true }).value
      } catch (e) {
        console.error(e);
        // Highlight failed for this language; fall back to HTML-escaped plain code
        return code
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
      }
    },
    // Function to get display name for programming language
    getLanguageDisplayName(language) {
      if (!language) return 'Code';

      const langKey = language.toLowerCase().trim();

      if (this.languageMap[langKey]) {
        return this.languageMap[langKey];
      }

      if (langKey.includes('script')) {
        return this.capitalizeFirst(language);
      }

      if (langKey.includes('sql')) {
        return language.toUpperCase();
      }

      if (langKey.includes('xml') || langKey.includes('html')) {
        return language.toUpperCase();
      }

      return this.capitalizeFirst(language);
    },

    capitalizeFirst(str) {
      if (!str) return 'Code';
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    },

    addLanguageMapping(key, displayName) {
      this.languageMap[key.toLowerCase()] = displayName;
    },

    getSupportedLanguages() {
      return Object.keys(this.languageMap).sort();
    },

    isLanguageSupported(language) {
      return this.languageMap.hasOwnProperty(language.toLowerCase());
    },
    async copyCodeBlock(code) {
      try {
        this.clipboardCopy(code);


        this.copiedStates[code] = true;

        // ฝลับเป็นไอคอนเดิมหลัง 3 วินาที
        setTimeout(() => {
          this.copiedStates[code] = false;
        }, 3000);
      } catch (err) {
        console.error('Copy operation failed:', err);
      }
    },
    clipboardCopy(text) {
      // Clean the text - remove blinking cursor if present
      let cleanText = text;
      if (cleanText.substring(cleanText.length - 47, cleanText.length) === '<span class="blinking-cursor" style="">|</span>') {
        cleanText = cleanText.substring(0, cleanText.length - 47);
      }

      // Create textarea element for copying
      let textArea = document.createElement('textarea');
      textArea.value = cleanText;
      textArea.focus();

      // Add to DOM temporarily
      document.body.appendChild(textArea);
      textArea.select();

      try {
        // Execute copy command
        const successful = document.execCommand('copy');
        if (successful) {
          console.log('Code copied to clipboard successfully');
          // Show success feedback if you have toast/notification system
          if (this.$toast) {
            this.$toast.success('Code copied to clipboard!');
          }
        } else {
          console.warn('Copy command was unsuccessful');
          if (this.$toast) {
            this.$toast.error('Failed to copy code');
          }
        }
      } catch (err) {
        console.error('Copy failed:', err);
        if (this.$toast) {
          this.$toast.error('Copy operation failed');
        }
      } finally {
        // Clean up - remove textarea
        textArea.remove();
      }
    },
    CopyCode() {
      // Extract text inside triple backticks
      const codeBlockRegex = /```([\s\S]*?)```/g;
      const matches = this.content.match(codeBlockRegex);

      if (matches && matches.length > 0) {
        // Remove the backticks and trim the content
        const codeToCopy = matches.map(match => match.replaceAll('```', '').trim()).join('\n\n');

        navigator.clipboard.writeText(codeToCopy)
          .then(() => {
            console.log("Copied code block to clipboard");
          })
          .catch(err => {
            console.error("Failed to copy", err);
          });
      } else {
        console.warn("No code block found to copy");
      }
    }
  }
}
</script>

<style scoped>
.inline-text-bg {
  display: inline-block;
  width: auto;
  background: #e8e8e8;
  padding: 1px 6px;
  border-radius: 6px;
  height: 24px;
  font-size: 15px;
  font-family: 'Fira Code', 'JetBrains Mono', 'DejaVu Sans Mono', monospace;
  color: #616161;
}

.inline-code-scrollable-block {
  display: block;
  width: 100%;
  max-width: 100%;
  background: #e8e8e8;
  padding: 8px 12px;
  border-radius: 6px;
  margin: 8px 0;
  overflow-x: auto;
  font-family: 'Fira Code', 'JetBrains Mono', 'DejaVu Sans Mono', monospace;
  font-size: 15px;
  color: #616161;
  box-sizing: border-box;
}

.inline-code-scrollable-block::-webkit-scrollbar {
  width: 15px;
  height: 15px;
}

.inline-code-scrollable-block::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.inline-code-scrollable-block::-webkit-scrollbar-thumb {
  background-color: #a0a0a0;
  border-radius: 10px;
  border: 2px solid #f1f1f1;
}

.inline-code-scrollable-block::-webkit-scrollbar-thumb:hover {
  background-color: #777;
}

.code-element::-webkit-scrollbar {
  width: 15px;
  height: 15px;
}

.code-element::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.code-element::-webkit-scrollbar-thumb {
  background-color: #a0a0a0;
  border-radius: 10px;
  border: 2px solid #f1f1f1;
}

.code-element::-webkit-scrollbar-thumb:hover {
  background-color: #777;
}

.markdown-container {
  width: 100% !important;
  margin: 0 !important;
  padding: 2px 0 4px;
  box-sizing: border-box;
  /* Fix image overflow */
  overflow: hidden;
}

/* Modern code block styling */
.code-block-modern {
  background: rgb(245, 245, 245);
  border-radius: 12px;
  overflow: hidden;
  margin: 12px 0;
  transition: all 0.3s ease;
}

/* Code header styling */
.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(221, 221, 221, 0.3);
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.code-language {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: #646e7c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.copy-btn {
  color: rgba(70, 70, 70, 0.562);
  background: transparent !important;
  transition: all 0.1s ease !important;
  box-shadow: none !important;
}

.copy-btn:hover {
  background-color: transparent !important;
  box-shadow: none !important;
  transform: scale(1.2);
}

/* Code content styling */
.code-content {
  position: relative;
  overflow-x: auto;
}

.code-pre {
  margin: 0 !important;
  padding: 0 20px !important;
  background: transparent !important;
  overflow-x: auto;
  font-size: 15px;
  line-height: 1.6;
}

.code-element {
  background: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
  border-radius: 0 !important;
  display: block;
}

:deep(.hljs-content) {
  margin: 0 !important;
  padding: 0px !important;
  background: transparent !important;
  overflow-x: auto;
}

:deep(.hljs) {
  background: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
  font-size: 14px;
  line-height: 1.6;
}

/* Existing styles for other elements */
.markdown {
  line-height: 1.8;
  margin-bottom: 0;
  padding-bottom: 0 !important;
}

:deep(.markdown p > img) {
  display: block;
  margin: 0 auto;
  padding: 0;
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

:deep(.markdown p) {
  margin: 0 !important;
  padding: 0 !important;
}

:deep(.markdown table) {
  display: block;
  max-width: 100%;
  overflow-x: scroll !important;
  border-collapse: collapse;
  white-space: nowrap;
  margin: 0;
  text-align: left;
  border-radius: 8px;
  overflow-y: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

:deep(.markdown tr),
:deep(.markdown th),
:deep(.markdown td) {
  border: 1px solid #e2e8f0 !important;
  padding: 12px;
  text-align: left;
  white-space: nowrap;
}

:deep(.markdown th) {
  background: linear-gradient(135deg, #5a70cf 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
}

:deep(.markdown tr:nth-child(even)) {
  background: rgba(0, 0, 0, 0.02);
}

:deep(code) {
  white-space: pre;
  background: rgba(0, 0, 0, 0.1);
  padding: 2px 6px;
  border-radius: 18px !important;
  font-family: 'Fira Code', 'JetBrains Mono', 'DejaVu Sans Mono', monospace;
  font-size: 0.9em;
}

:deep(pre) {
  overflow-x: auto;
  background-color: transparent;
}

:deep(.markdown-container pre) {
  overflow-x: auto;
  background-color: transparent;
}

:deep(.markdown strong) {
  font-weight: 600 !important;
  color: #2d3748;
}

/* Animation for copy feedback */
@keyframes copySuccess {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.2);
  }

  100% {
    transform: scale(1);
  }
}

.copy-success {
  animation: copySuccess 0.3s ease;
}

/* Responsive design */
@media (max-width: 768px) {
  .code-header {
    padding: 8px 12px;
  }

  :deep(.hljs) {
    font-size: 12px;
  }
}

/* เพิ่ม CSS สำหรับตาราง */
.table-container {
  width: 100%;
  overflow-x: auto;
  margin: 16px 0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;
}

.table-container::-webkit-scrollbar {
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.table-data-cell.short-content {
  max-width: 120px;
  min-width: 80px;
}

.table-data-cell.long-content {
  max-width: 300px;
  min-width: 150px;
}

.markdown-table {
  width: 100%;
  min-width: 800px;
  /* เพิ่มขนาดขั้นต่ำ */
  border-collapse: collapse;
  font-size: 14px;
  background-color: white;
  margin: 0;
  table-layout: auto;
  /* ให้ table ปรับขนาด column อัตโนมัติ */
}

.table-header-cell {
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  padding: 12px 8px;
  font-weight: 600;
  text-align: left;
  color: #333;
  border-bottom: 2px solid #ddd;
  white-space: normal;
  /* เปลี่ยนจาฝ nowrap */
  word-wrap: break-word;
  min-width: 100px;
  max-width: 200px;
  line-height: 1.3;
}

.table-data-cell {
  border: 1px solid #eee;
  padding: 10px 8px;
  text-align: left;
  vertical-align: top;
  white-space: normal;
  /* เปลี่ยนจาฝ nowrap เป็น normal */
  word-wrap: break-word;
  /* เพิ่มฝารตัดคำ */
  /* เพิ่มฝารตัดคำในฝรณีคำยาว */
  overflow-wrap: break-word;
  /* รองรับ browser เฝ่า */
  min-width: 100px;
  /* ฝำหนดความฝว้างขั้นต่ำ */
  max-width: 200px;
  /* เพิ่มความฝว้างสูงสุด */
  line-height: 1.4;
  /* ปรับ line height ให้อ่านง่าย */
}

.table-head-row {
  background-color: #f5f5f5;
}

.table-body-row:nth-child(even) {
  background-color: #f8f9fa;
}

.table-body-row:hover {
  background-color: #f9f9f9;
  transition: background-color 0.2s ease;
}

.table-body-row:last-child .table-data-cell {
  border-bottom: none;
}

.table-data-cell:first-child {
  min-width: 120px;
  /* คอลัมน์๝รฝฝว้างฝว่า */
  max-width: 180px;
}

.table-data-cell:last-child {
  min-width: 150px;
  /* คอลัมน์สุดท้ายฝว้างฝว่า */
  max-width: 250px;
}

/* เพิ่มสไตล์สำหรับข้อความที่ยาวมาฝ */
.table-data-cell.extra-long {
  max-width: 400px;
  min-width: 200px;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .table-container {
    margin: 12px 0;
  }

  .markdown-table {
    font-size: 12px;
    min-width: 500px;
  }

  .table-header-cell,
  .table-data-cell {
    padding: 8px 6px;
    min-width: 80px;
    max-width: 150px;
    font-size: 11px;
    line-height: 1.3;
  }
}

/* ปรับ๝ต่งให้ table ใน message box สวยขึ้น */
.markdown-container .table-container {
  max-width: 100%;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}


:deep(.markdownImg p > img) {
  display: block;
  margin: 0 auto;
  padding: 0;
  max-width: 300px;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

/* ปรับ indent ของ list */
.markdown-container :deep(ul),
.markdown-container :deep(ol) {
  padding-left: 1.5em;
  margin-top: 0;
  margin-bottom: 1em;
}

/* เว้นระยะห่างระหว่าง items */
.markdown-container :deep(li) {
  margin-bottom: 0.5em;
}

@keyframes fadeInElement {
  from {
    opacity: 0.3;
    transform: translateY(3px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.markdown-container :deep(.markdownImg > *:last-child) {
  animation: fadeInElement 0.3s ease-out;
}

/* อัพเดต CSS สำหรับ file display */
.markdown-container :deep(.file-display-wrapper) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background-color: #f9fafb;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  margin: 2px;
  vertical-align: middle;
}

.markdown-container :deep(.file-icon) {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  vertical-align: middle;
}

.markdown-container :deep(.filename) {
  font-size: 0.85em;
  color: #555;
  white-space: nowrap;
  font-family: system-ui, -apple-system, sans-serif;
}

.markdown-container :deep(a) {
  display: inline;
  margin: 0;
  padding: 0;
  color: rgb(171, 187, 226);
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.4s ease;
  vertical-align: baseline;
}

:deep(.markdownImg) {
  width: 100%;
  overflow: hidden; /* ✅ Prevent image overflow */
}

:deep(.markdownImg p > img),
:deep(.markdownImg img) {
  display: block;
  margin: 0 auto;
  padding: 0;
  max-width: 300px !important;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  object-fit: contain; /* ✅ Maintain aspect ratio */
}

/* For specific size control */
:deep(.markdownImg p > img.thumbnail),
:deep(.markdownImg img.thumbnail) {
  max-width: 300px;
}

/* Responsive images */
@media (max-width: 768px) {
  :deep(.markdownImg p > img),
  :deep(.markdownImg img) {
    max-width: 100% !important;
    width: 100%;
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
